import React, { useEffect, useMemo } from "react";
import { SectionTitle } from "./SectionTitle";
import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from "@mui/material";

import { CheckoutFormTypeFields, useCheckoutForm } from "./hooks/useCheckoutForm";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";

import {
    OrderPaymentOptionViewDTO,
    OrderPaymentOptionViewDTOFields,
} from "../../types/purchaseOrder/Order";
import { STYLES } from "../ShoppingCart/ShoppingCartStyles";

const MP_LOGO_URL = "images/Mercado_Pago.svg";


const isMercadoPagoProvider = (providerCode?: number | null) =>
    Number(providerCode) === 1;

export const PaymentOptionSection: React.FC = () => {
    const {
        confirmedZipCode,
        methods,
        paymentOptions,
        isLoadingPaymentOptions,
        paymentOptionId,
        setPaymentOptionId,
    } = useCheckoutForm();

    useEffect(() => {
        methods.register(CheckoutFormTypeFields.PaymentOptionId, {
            required: "Seleccioná un método de pago.",
        });
    }, [methods]);

    const flatOptions = useMemo<OrderPaymentOptionViewDTO[]>(() => {
        const list = [...(paymentOptions ?? [])];

        list.sort((a: any, b: any) => {
            const aIsMp = isMercadoPagoProvider(
                a?.[OrderPaymentOptionViewDTOFields.ProviderCode]
            );
            const bIsMp = isMercadoPagoProvider(
                b?.[OrderPaymentOptionViewDTOFields.ProviderCode]
            );
            if (aIsMp === bIsMp) return 0;
            return aIsMp ? -1 : 1;
        });

        return list;
    }, [paymentOptions]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentOptionId(e.target.value); // GUID
    };

    const errorMsg =
        (methods.formState.errors as any)?.[CheckoutFormTypeFields.PaymentOptionId]
            ?.message ?? null;

    if (!confirmedZipCode) return null;

    return (
        <Stack mb={2}>
            <SectionTitle>Método de pago</SectionTitle>

            {isLoadingPaymentOptions ? (
                <Typography sx={TYPOGRAPHY_STYLES.rubikSmall} color="rgba(1,1,1,.6)">
                    Cargando métodos de pago...
                </Typography>
            ) : (
                <>
                    <RadioGroup value={paymentOptionId ?? ""} onChange={onChange}>
                        <Stack spacing={1.25}>
                            {flatOptions.map((opt: any) => (
                                <PaymentOptionItem
                                    key={String(opt?.id ?? "")}
                                    value={String(opt?.id ?? "")} // GUID
                                    title={String(
                                        opt?.[OrderPaymentOptionViewDTOFields.OptionName] ?? ""
                                    )}
                                    description={String(
                                        opt?.[OrderPaymentOptionViewDTOFields.Description] ?? ""
                                    )}
                                    providerCode={Number(
                                        opt?.[OrderPaymentOptionViewDTOFields.ProviderCode] ?? 0
                                    )}
                                />
                            ))}
                        </Stack>
                    </RadioGroup>

                    {errorMsg && (
                        <Typography color="error" fontSize={12} mt={0.5}>
                            {errorMsg}
                        </Typography>
                    )}
                </>
            )}
        </Stack>
    );
};

interface PaymentOptionItemProps {
    value: string; // GUID
    title: string;
    description: string;
    providerCode: number;
}

const PaymentOptionItem: React.FC<PaymentOptionItemProps> = ({
    value,
    title,
    description,
    providerCode,
}) => {
    const isMP = isMercadoPagoProvider(providerCode);

    return (
        <Box sx={{ border: "1px solid #f0f0f0", mb: 1, p: 1 }}>
            <FormControlLabel
                value={value}
                control={<Radio />}
                sx={{
                    width: "100%",
                    "& > .MuiFormControlLabel-label": { width: "100%" },
                }}
                label={
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        width={1}
                        paddingY={1}
                        alignItems="center"
                        sx={{ gap: 2 }}
                    >
                        {/* IZQUIERDA */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isMP ? (
                                // MP: solo logo grande, sin textos
                                <Box
                                    component="img"
                                    src={MP_LOGO_URL}
                                    alt="Mercado Pago"
                                    loading="lazy"
                                    sx={{
                                        height: 44,
                                        width: "auto",
                                        display: "block",
                                        maxWidth: "100%",
                                    }}
                                />
                            ) : (
                                <Stack sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        sx={{
                                            color: "#1b1b1b",
                                            fontSize: "14px",
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        {title}
                                    </Typography>

                                    {!!description && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "#1b1b1b",
                                                opacity: 0.8,
                                                fontSize: "13px",
                                                fontFamily: "inherit",
                                                whiteSpace: "normal",
                                                overflowWrap: "anywhere",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        </Box>

                        <Typography
                            variant="h4"
                            fontWeight={600}
                            sx={{
                                color: STYLES.colors.secondary,
                                fontSize: "16px",
                                fontFamily: "inherit",
                                opacity: 0,
                            }}
                        >
                            .
                        </Typography>
                    </Stack>
                }
            />
        </Box>
    );
};














